import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useJoinOrganization from "../../queries/routes/useJoinOrganization";
import useCreateUser from "../../queries/routes/useCreateUser";
import Box from "@mui/material/Box/Box";
import { Avatar, Button, Checkbox, Grid, TextField } from "@mui/material";
import GenieText from "../../components/core/genie-text/GenieText";
import useUser from "../../queries/routes/useUser";
import GenieProcessing from "../../components/core/genie-processing/GenieProcessing";
import useAcceptInvite from "../../queries/routes/useAcceptInvite";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import GenieSpinner from "../../components/core/genie-spinner/GenieSpinner";

function BuyerRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invite_id =
    searchParams.get("invite_id") || localStorage.getItem("buyer_invite_id");
  const org_id =
    searchParams.get("org_id") || localStorage.getItem("buyer_org_id");

  if (invite_id) {
    localStorage.setItem("buyer_invite_id", invite_id);
  }
  if (org_id) {
    // the id of the org
    localStorage.setItem("buyer_org_id", org_id);
  }

  const { data: user } = useUser();

  const defaultValues = {
    name: "",
    terms: true,
    email: true,
  };
  const [formValues, setFormValues] = useState(defaultValues);

  const { mutate: acceptInvite } = useAcceptInvite(invite_id!);
  const { mutate: joinOrganization, isLoading: isLoadingJoinOrganization } =
    useJoinOrganization();
  const {
    mutate: createUser,
    isSuccess,
    isLoading: isCreateUserLoading,
  } = useCreateUser();

  if (!invite_id || !org_id) {
    navigate("/404");
  }

  useEffect(() => {
    createUser();
  }, [createUser]);

  useEffect(() => {
    if (isSuccess) {
      acceptInvite();
    }
  }, [isSuccess, acceptInvite]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: checked,
    });
  };

  if (isLoadingJoinOrganization || isCreateUserLoading) {
    return (
      <GenieProcessing
        phrases={[
          "We are setting up your account!",
          "Your wholesale process is about to be much simpler.",
        ]}
      />
    );
  }

  return (
    <Box display={"flex"} height={"100vh"} width={"100vw"}>
      <Box
        sx={{
          flexBasis: 1000,
          marginLeft: "5%",
          marginRight: "5%",
        }}
        maxWidth={400}
        height={"100%"}
        display={"flex"}
        alignItems={"center"}
      >
        <Box mr={1}>
          <img
            height={"40px"}
            src={`/assets/genie_logo.svg`}
            alt={"Genie Logo"}
          />

          <form
            onSubmit={() =>
              joinOrganization(
                {
                  selected_type: "Buyer",
                  name: formValues.name,
                },
                {
                  onSuccess: () => {
                    navigate(`/storefront/${org_id}`);
                  },
                }
              )
            }
          >
            <Box mt={2}>
              <Grid container>
                <Grid item xs={12}>
                  {user && user.email && (
                    <Box my={4} display={"flex"} alignItems={"center"}>
                      <Box width={"100%"}>
                        <TextField
                          id="email"
                          label="Your email"
                          variant="outlined"
                          required
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <Box mr={2}>
                                <Avatar src={user.picture} alt={user.name} />
                              </Box>
                            ),
                          }}
                          fullWidth
                          name="name"
                          value={user.email}
                          onChange={handleInputChange}
                        />
                      </Box>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box mb={4}>
                    <TextField
                      id="name"
                      label="Your business name"
                      variant="outlined"
                      required
                      fullWidth
                      focused
                      autoFocus
                      name="name"
                      value={formValues.name}
                      onChange={handleInputChange}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Grid item xs={12}>
                    <Box mb={4}>
                      <Box display={"flex"} alignItems={"flex-end"}>
                        <Checkbox
                          id="agree-terms"
                          name={"terms"}
                          defaultChecked
                          value={formValues.terms}
                          inputProps={{ "aria-label": "controlled" }}
                          onChange={handleCheckboxChange}
                        />
                        <GenieText subdued paragraph fontSize={"tiny"}>
                          By signing up, you agree to our Terms and privacy
                          policy.
                        </GenieText>
                      </Box>
                      <Box display={"flex"} alignItems={"flex-end"}>
                        <Checkbox
                          id="send-emails"
                          name={"email"}
                          defaultChecked
                          value={formValues.email}
                          inputProps={{ "aria-label": "controlled" }}
                          onChange={handleCheckboxChange}
                        />
                        <GenieText subdued fontSize={"tiny"}>
                          Yes, please send me transactional emails related to my
                          account. I understand that I can opt-out at anytime.
                        </GenieText>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box mb={4}>
                    <Button
                      variant={"outlined"}
                      size={"large"}
                      type={"submit"}
                      disabled={
                        !formValues.terms ||
                        formValues.name === "" ||
                        isLoadingJoinOrganization
                      }
                      fullWidth
                    >
                      Finish Creating Your Account
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </form>
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "100%",
          backgroundImage: `url("/assets/account.jpg")`,
        }}
      />
    </Box>
  );
}

export default withAuthenticationRequired(BuyerRedirect, {
  onRedirecting: () => <GenieSpinner />,
});
